/**
 * What moved since the screen last looked. The API sends, per place, the pages that left in each of the last
 * twelve whole 5 s steps and when the last step ended; the screen remembers the end it last drew and animates
 * only the steps after it. So a page crosses a track because a unit finished, and a quiet system draws nothing.
 * Pure.
 */
import type { AdminFlow, FlowPlace } from './flow-types';

/** Pages that left a place in the steps the screen had not drawn yet; `key` restarts the animation. */
export interface FlowBurst {
  pages: number;
  key: string;
}

export type FlowBursts = Partial<Record<FlowPlace, FlowBurst>>;

/** Whole steps between the end last drawn and this one; none on the first look, all twelve after a long gap. */
export function stepsSince(previousEnd: string | null, flow: AdminFlow): number {
  if (!previousEnd) return 0;
  const gap = (Date.parse(flow.tick.end) - Date.parse(previousEnd)) / 1000;
  if (!(gap > 0)) return 0;
  return Math.min(12, Math.round(gap / flow.tick.seconds));
}

export function burstsOf(previousEnd: string | null, flow: AdminFlow): FlowBursts {
  const steps = stepsSince(previousEnd, flow);
  const bursts: FlowBursts = {};
  if (steps === 0) return bursts;
  for (const cell of flow.cells) {
    const pages = cell.ticks.slice(-steps).reduce((n, p) => n + p, 0);
    if (pages > 0) bursts[cell.place] = { pages, key: flow.tick.end };
  }
  return bursts;
}

/** How many marks cross a track for a burst: one for a page, one more each time the pages double, seven at most. */
export function tokensFor(pages: number): number {
  if (pages <= 0) return 0;
  return Math.min(7, 1 + Math.floor(Math.log2(pages)));
}
